// src/utils/cnCodeLookup.js
//
// Lookup helpers for CBAM CN codes.
// Data source: official CBAM Communication template, sheet "Parameters_CNCodes".
// 569 codes mapped to 18 CBAM goods categories.

import cnCodesData from '../data/cnCodes.json';

const ALL_CODES = cnCodesData.cn_codes;

// Alias map for category-name mismatches between FormA's goods list and
// the CBAM template's CN code categories.
// FormA name → CN data name.
const CATEGORY_ALIASES = {
  'Electricity': 'Electricity (export to EU)',
};

/**
 * Returns all CN codes that belong to a given CBAM goods category.
 *
 * @param {string} category - The goods category name (e.g. "Iron or steel products").
 * @returns {Array<{code: string, code_compact: string, description: string, cbam_good: string}>}
 *   Returns [] if the category is unknown or empty.
 */
export const getCnCodesForGood = (category) => {
  if (!category || typeof category !== 'string') return [];
  const resolved = CATEGORY_ALIASES[category] || category;
  return ALL_CODES.filter((entry) => entry.cbam_good === resolved);
};

/**
 * Returns the official description for a given CN code, matched on the
 * SPACED form (e.g. "7208 10 00").
 *
 * @param {string} code - The CN code (e.g. "7208 10 00").
 * @returns {string} The description, or "" if not found.
 */
export const getDescriptionForCode = (code) => {
  if (!code || typeof code !== 'string') return '';
  const trimmed = code.trim();
  const entry = ALL_CODES.find((e) => e.code === trimmed);
  return entry ? entry.description : '';
};

/**
 * Returns the official description for a given CN code, matched on the
 * COMPACT form (e.g. "72081000") — this is the format actually stored
 * in Form A's aggregatedGoods[].cnCode field.
 *
 * @param {string} codeCompact - Compact CN code, no spaces (e.g. "72081000").
 * @returns {string} The description, or "" if not found.
 */
export const getDescriptionForCompactCode = (codeCompact) => {
  if (!codeCompact || typeof codeCompact !== 'string') return '';
  const entry = ALL_CODES.find((e) => e.code_compact === codeCompact.trim());
  return entry ? entry.description : '';
};

/**
 * Returns the properly spaced/displayed CN code for a given compact code.
 *
 * @param {string} codeCompact - Compact CN code (e.g. "72081000").
 * @returns {string} The spaced display form (e.g. "7208 10 00"), or the
 *   input unchanged if not found.
 */
export const getDisplayCodeForCompact = (codeCompact) => {
  if (!codeCompact || typeof codeCompact !== 'string') return codeCompact || '';
  const entry = ALL_CODES.find((e) => e.code_compact === codeCompact.trim());
  return entry ? entry.code : codeCompact;
};

/**
 * Returns a combined "code — description" label for a compact CN code.
 * Convenience wrapper for dropdown option text / tooltips.
 *
 * @param {string} codeCompact - Compact CN code (e.g. "72081000").
 * @returns {string}
 */
export const getCnLabel = (codeCompact) => {
  const entry = ALL_CODES.find((e) => e.code_compact === (codeCompact || '').trim());
  if (!entry) return codeCompact || '';
  return `${entry.code} — ${entry.description}`;
};

/**
 * Returns the full list of unique CBAM goods categories in the dataset.
 *
 * @returns {Array<string>}
 */
export const getAllCnCategories = () => {
  return [...new Set(ALL_CODES.map((e) => e.cbam_good))].sort();
};

/**
 * Returns ALL CN codes (use when no aggregated good is set — fallback).
 *
 * @returns {Array<{code: string, code_compact: string, description: string, cbam_good: string}>}
 */
export const getAllCnCodes = () => ALL_CODES;